import React, { useEffect, useRef } from "react";
import { TopNavProps } from "./TopNav.types";
import {
  TopNavContainer,
  StyledNavIcon,
  NavIcon,
  NavTextWrapper,
  NavArrowWrapper,
  NavIconContainer,
  SupportIconWrapper,
  SupportIcon,
  HeadingWrapper,
} from "./TopNav.styles";
import {
  Body5MediumTypography,
  Heading4SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";
import { colorGuide } from "../../theme/colors";
import { Icon } from "../Icon/Icon";
import { Skeleton } from "@mui/material";
import Box from "../../primitives/Box/Box";
import { ActionButton } from "../ActionButton/ActionButton";
import Tags from "../Tags/Tags";

export const TopNav = ({
  variant,
  title,
  subtitle,
  showBackArrow,
  showEdit,
  showUnhide,
  onLogoClick,
  onClick,
  hideVariant,
  onEditClick,
  onUnhideClick,
  icon,
  icon2,
  icon3,
  statusTag,
  infoTag,
  bgColor,
  scrollBackgroundColor,
  isLoading,
  color,
  nonSticky,
  showButton,
  button,
  dataCy,
  isWebview = false, // Default to false for webview
  fixed = false,
  rightChildren,
  shadowDisabled = false,
  borderDisabled = false,
  subtitleFontWeight = "500",
}: TopNavProps) => {
  const isSupportIconVisible = showEdit || showUnhide || !!icon || showButton;
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current || shadowDisabled) return;

      if (window.scrollY > 0) {
        headerRef.current.style.boxShadow =
          "0 4px 8px -4px rgba(0, 0, 0, 0.08), 0 8px 16px -8px rgba(0, 0, 0, 0.06), 0 3px 6px -3px rgba(0, 0, 0, 0.04)";

        if (scrollBackgroundColor) {
          headerRef.current.style.backgroundColor = scrollBackgroundColor;
        }
      } else {
        headerRef.current.style.boxShadow = "none";
        if (scrollBackgroundColor) {
          headerRef.current.style.backgroundColor = bgColor || "transparent";
        }
      }
    };

    if (!nonSticky && !shadowDisabled) {
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (!nonSticky && !shadowDisabled) {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, [nonSticky, scrollBackgroundColor, bgColor]);
  const renderContent = () => {
    switch (variant) {
      case "logo":
        return (
          <StyledNavIcon
            className="fa-kit fa-wishlink-icon"
            role="img"
            aria-label="Wishlink Logo"
            color={color}
            onClick={onLogoClick}
          />
        );
      case "back":
        return (
          <>
            <NavIcon
              className="fa-kit fa-nav-arrow"
              onClick={onClick}
              role="button"
              aria-label="Go Back"
              tabIndex={0}
              color={color}
              data-cy={dataCy}
            />
            {isSupportIconVisible && (
              <SupportIconWrapper>
                {showUnhide && (
                  <Icon
                    onClick={onUnhideClick}
                    height="20"
                    width="20"
                    size="16"
                    name={`fa-sharp fa-solid ${
                      hideVariant === "delete"
                        ? "fa-trash"
                        : hideVariant === "hide"
                        ? "fa-eye-slash"
                        : "fa-eye"
                    }`}
                    color={
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}
                {showEdit && (
                  <Icon
                    onClick={onEditClick}
                    height="20"
                    width="20"
                    size="16"
                    name="fa-sharp fa-solid fa-pen"
                    color={
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}
                {icon && (
                  <Icon
                    onClick={icon.onClick}
                    height="20"
                    width="20"
                    size="16"
                    name={icon.name}
                    state={icon.state || "default"}
                    count={icon.count}
                    notificationColor={icon.notificationColor}
                    countColor={icon.countColor}
                    countTextColor={icon.countTextColor}
                    color={
                      icon.color ||
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}
                {icon2 && (
                  <Icon
                    onClick={icon2.onClick}
                    height="20"
                    width="20"
                    size="16"
                    name={icon2.name}
                    color={
                      icon2.color ||
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                    state={icon2.state || "default"}
                    count={icon2.count}
                    notificationColor={icon2.notificationColor}
                    countColor={icon2.countColor}
                    countTextColor={icon2.countTextColor}
                    top={icon2.top}
                    right={icon2.right}
                    bottom={icon2.bottom}
                    left={icon2.left}
                  />
                )}
                {icon3 && (
                  <Icon
                    onClick={icon3.onClick}
                    height="20"
                    width="20"
                    size="16"
                    name={icon3.name}
                    color={
                      icon3.color ||
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                    state={icon3.state || "default"}
                    count={icon3.count}
                    notificationColor={icon3.notificationColor}
                    countColor={icon3.countColor}
                    countTextColor={icon3.countTextColor}
                    top={icon3.top}
                    right={icon3.right}
                    bottom={icon3.bottom}
                    left={icon3.left}
                  />
                )}
              </SupportIconWrapper>
            )}

            {showButton && (
              <ActionButton
                variant={button?.variant || "primary"}
                className={button?.className}
                onClick={button?.onClick}
                disabled={button?.isDisabled}
                leftIcon={
                  button?.leftIcon && (
                    <Icon
                      height="20"
                      width="20"
                      size="16"
                      name={button?.leftIcon?.name}
                      color={button?.leftIcon?.color}
                    />
                  )
                }
                rightIcon={
                  button?.rightIcon && (
                    <Icon
                      name={button?.rightIcon?.name}
                      color={button?.rightIcon?.color}
                    />
                  )
                }
              >
                {button?.text}
              </ActionButton>
            )}
          </>
        );
      case "close":
        return (
          <SupportIcon
            data-cy={dataCy}
            className="fa-solid fa-xmark"
            onClick={onClick}
            role="button"
            aria-label="Close"
            tabIndex={0}
            color={color}
          />
        );
      case "title-with-status":
        return !showBackArrow && !isSupportIconVisible ? (
          <>
            {isLoading ? (
              <Skeleton width={140} />
            ) : (
              <Heading4SemiboldTypography
                fontFamily="Lexend"
                color={color || "#000"}
              >
                {title}
              </Heading4SemiboldTypography>
            )}
            {isLoading && subtitle ? (
              <Skeleton width={140} />
            ) : (
              <Body5MediumTypography color={color || "#000"}>
                {subtitle}
              </Body5MediumTypography>
            )}
          </>
        ) : (
          <NavArrowWrapper>
            {showBackArrow && (
              <NavIconContainer>
                <NavIcon
                  className="fa-kit fa-nav-arrow"
                  onClick={onClick}
                  role="button"
                  aria-label="Go Back"
                  tabIndex={0}
                  color={color}
                  data-cy={dataCy}
                />
              </NavIconContainer>
            )}

            <NavTextWrapper>
              <HeadingWrapper>
                {isLoading ? (
                  <Skeleton width={140} />
                ) : (
                  <Heading4SemiboldTypography
                    fontFamily="Lexend"
                    color={color || "#000"}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxWidth="16rem"
                  >
                    {title}
                  </Heading4SemiboldTypography>
                )}
                {showEdit && (
                  <Icon
                    onClick={onEditClick}
                    height="20"
                    width="20"
                    size="16"
                    name="fa-sharp fa-solid fa-pen"
                    color={
                      color ||
                      colorGuide.lightComponents.topNav.supportIconColor
                    }
                  />
                )}
                {icon && (
                  <Box className={icon.iconClassName}>
                    <Icon
                      height="20"
                      width="20"
                      size="20"
                      name={icon.name}
                      state={icon.state || "default"}
                      count={icon.count}
                      notificationColor={icon.notificationColor}
                      countColor={icon.countColor}
                      countTextColor={icon.countTextColor}
                      onClick={icon.onClick}
                      dataCy={`${dataCy}-right-icon`}
                      color={
                        icon.color ||
                        color ||
                        colorGuide.lightComponents.topNav.supportIconColor
                      }
                      top={icon.top}
                      right={icon.right}
                      bottom={icon.bottom}
                      left={icon.left}
                    />
                  </Box>
                )}
              </HeadingWrapper>

              {isLoading && statusTag ? (
                <Skeleton width={140} />
              ) : (
                <Tags
                  variant="primary"
                  label={statusTag?.label || ""}
                  backgroundColor={statusTag?.backgroundColor}
                  color={statusTag?.textColor}
                  icon={statusTag?.icon}
                  iconColor={statusTag?.iconColor}
                />
              )}
            </NavTextWrapper>
          </NavArrowWrapper>
        );
      case "title-subtitle":
        return !showBackArrow && !isSupportIconVisible && !rightChildren ? (
          <>
            {isLoading ? (
              <Skeleton width={140} />
            ) : (
              <Heading4SemiboldTypography
                fontFamily="Lexend"
                color={color || "#000"}
              >
                {title}
              </Heading4SemiboldTypography>
            )}
            {isLoading && subtitle ? (
              <Skeleton width={140} />
            ) : (
              <Body5MediumTypography color={color || "#000"}>
                {subtitle}
              </Body5MediumTypography>
            )}
          </>
        ) : (
          <NavArrowWrapper>
            {showBackArrow && (
              <NavIconContainer>
                <NavIcon
                  className="fa-kit fa-nav-arrow"
                  onClick={onClick}
                  role="button"
                  aria-label="Go Back"
                  tabIndex={0}
                  color={color}
                  data-cy={dataCy}
                />
              </NavIconContainer>
            )}

            <NavTextWrapper>
              <HeadingWrapper>
                {isLoading ? (
                  <Skeleton width={140} />
                ) : (
                  <Heading4SemiboldTypography
                    fontFamily="Lexend"
                    color={color || "#000"}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxWidth="14rem"
                  >
                    {title}
                  </Heading4SemiboldTypography>
                )}

                <SupportIconWrapper>
                  {showEdit && (
                    <Icon
                      onClick={onEditClick}
                      height="20"
                      width="20"
                      size="16"
                      name="fa-sharp fa-solid fa-pen"
                      color={
                        color ||
                        colorGuide.lightComponents.topNav.supportIconColor
                      }
                    />
                  )}
                  {icon && (
                    <Icon
                      onClick={icon.onClick}
                      height="20"
                      width="20"
                      size="20"
                      name={icon.name}
                      state={icon.state || "default"}
                      count={icon.count}
                      notificationColor={icon.notificationColor}
                      countColor={icon.countColor}
                      countTextColor={icon.countTextColor}
                      dataCy={`${dataCy}-right-icon`}
                      color={
                        icon.color ||
                        color ||
                        colorGuide.lightComponents.topNav.supportIconColor
                      }
                      top={icon.top}
                      right={icon.right}
                      bottom={icon.bottom}
                      left={icon.left}
                    />
                  )}
                  {icon2 && (
                    <Icon
                      onClick={icon2.onClick}
                      height="20"
                      width="20"
                      size="16"
                      name={icon2.name}
                      color={
                        icon2.color ||
                        color ||
                        colorGuide.lightComponents.topNav.supportIconColor
                      }
                      state={icon2.state || "default"}
                      count={icon2.count}
                      notificationColor={icon2.notificationColor}
                      countColor={icon2.countColor}
                      countTextColor={icon2.countTextColor}
                      top={icon2.top}
                      right={icon2.right}
                      bottom={icon2.bottom}
                      left={icon2.left}
                    />
                  )}
                  {icon3 && (
                    <Icon
                      onClick={icon3.onClick}
                      height="20"
                      width="20"
                      size="16"
                      name={icon3.name}
                      color={
                        icon3.color ||
                        color ||
                        colorGuide.lightComponents.topNav.supportIconColor
                      }
                      state={icon3.state || "default"}
                      count={icon3.count}
                      notificationColor={icon3.notificationColor}
                      countColor={icon3.countColor}
                      countTextColor={icon3.countTextColor}
                      top={icon3.top}
                      right={icon3.right}
                      bottom={icon3.bottom}
                      left={icon3.left}
                    />
                  )}
                </SupportIconWrapper>
                {infoTag && (
                  <Tags
                    variant="primary"
                    label={infoTag.label}
                    backgroundColor={infoTag.backgroundColor}
                    color={infoTag.textColor}
                    icon={infoTag.icon}
                    iconColor={infoTag.iconColor}
                    onClick={infoTag.onClick}
                  />
                )}
                {rightChildren && rightChildren}
              </HeadingWrapper>

              {isLoading && subtitle ? (
                <Skeleton width={140} />
              ) : (
                <Body5MediumTypography
                  color={color || "#000"}
                  fontWeight={subtitleFontWeight}
                >
                  {subtitle}
                </Body5MediumTypography>
              )}
            </NavTextWrapper>

            {showButton && (
              <ActionButton
                variant={button?.variant || "primary"}
                className={button?.className}
                onClick={button?.onClick}
                disabled={button?.isDisabled}
                leftIcon={
                  button?.leftIcon && (
                    <Icon
                      height="20"
                      width="20"
                      size="16"
                      name={button?.leftIcon?.name}
                      color={button?.leftIcon?.color}
                    />
                  )
                }
                rightIcon={
                  button?.rightIcon && (
                    <Icon
                      name={button?.rightIcon?.name}
                      color={button?.rightIcon?.color}
                    />
                  )
                }
              >
                {button?.text}
              </ActionButton>
            )}
          </NavArrowWrapper>
        );
      default:
        return null;
    }
  };

  return (
    <TopNavContainer
      ref={headerRef}
      variant={variant}
      showBackArrow={showBackArrow}
      subtitle={subtitle}
      isSupportIconVisible={isSupportIconVisible}
      bgColor={bgColor}
      scrollBackgroundColor={scrollBackgroundColor}
      sticky={nonSticky}
      isWebview={isWebview}
      fixed={fixed}
      borderDisabled={borderDisabled}
    >
      {renderContent()}
    </TopNavContainer>
  );
};
